import { memo, useEffect, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { ChevronRight, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Avatar,
  DeleteTeamButton,
  EmptySlot,
  FilledSlot,
  NeedsPartnerTag,
} from "./TeamsBoardParts";
import {
  TEAM_NAME_MAX,
  teamEqual,
  teamNamesEqual,
  type BoardId,
  type BoardTeam,
  validateTeamName,
} from "./teamsBoardModel";

/**
 * Drop-target id for one slot. Only ever used as an identity — the team and
 * slot travel in the droppable's `data`, never parsed back out of this string.
 * The ids the API returns are opaque (strings like "Wkp2zapg"), so decoding
 * them out of a composite key is exactly the kind of assumption that breaks.
 */
export const slotDropId = (teamId: BoardId, index: number) =>
  `slot:${String(teamId)}:${index}`;

/** What a slot droppable carries, read straight back on drop. */
export type SlotDropData = { teamId: BoardId; index: number };

/** One empty slot, registered with dnd-kit as a drop target. */
function DroppableEmptySlot({
  teamId,
  index,
  isArmed,
  onPlace,
  label = "Drop player here",
}: {
  teamId: BoardId;
  index: number;
  isArmed: boolean;
  onPlace?: () => void;
  label?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: slotDropId(teamId, index),
    // The team and slot ride along as data; nothing parses them back out of
    // the id string, whose ids are opaque and not necessarily numeric.
    data: { teamId, index } satisfies SlotDropData,
  });
  return (
    <EmptySlot
      innerRef={setNodeRef}
      isOver={isOver}
      isArmed={isArmed}
      onClick={onPlace}
      label={isArmed ? "Place here" : label}
    />
  );
}

/** Click-to-edit team name. Commits on blur or Enter; Escape reverts. */
function TeamNameEditor({
  team,
  teams,
  onRename,
}: {
  team: BoardTeam;
  teams: BoardTeam[];
  onRename: (name: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(team.name);
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const problem = validateTeamName(value, teams, team.teamId);
    if (problem) {
      setError(problem);
      return;
    }
    setEditing(false);
    setError(undefined);
    const trimmed = value.trim();
    if (trimmed !== team.name) onRename(trimmed);
  };

  const cancel = () => {
    setValue(team.name);
    setError(undefined);
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setValue(team.name);
          setEditing(true);
        }}
        className="group flex min-w-0 items-center gap-1.5 rounded-md border-0 bg-transparent p-0 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <span className="poppins text-base font-semibold break-words text-text-primary">
          {team.name}
        </span>
        <Pencil
          className="size-3 shrink-0 text-text-muted"
          aria-hidden
        />
        <span className="sr-only">Rename team</span>
      </button>
    );
  }

  return (
    <span className="min-w-0 flex-1">
      <input
        ref={inputRef}
        aria-label="Team name"
        value={value}
        maxLength={TEAM_NAME_MAX}
        aria-invalid={!!error || undefined}
        onChange={(e) => {
          setValue(e.target.value);
          if (error) setError(undefined);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        className="poppins w-full rounded-md border border-border bg-surface px-2 py-1 text-base font-semibold text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
      />
      {error && (
        <span className="mulish mt-1 block text-xs text-destructive">
          {error}
        </span>
      )}
    </span>
  );
}

/** Matches .animate-team-complete's duration in index.css. */
const PULSE_MS = 600;

/**
 * Whether an animationend belongs to the element handling it.
 *
 * animationend bubbles, so a slot's 220ms arrival animation reaches the card
 * and would end the card's 600ms completion pulse a third of the way through —
 * and a slot arriving is exactly when that pulse runs.
 */
export function isOwnAnimationEnd(e: {
  target: unknown;
  currentTarget: unknown;
}): boolean {
  return e.target === e.currentTarget;
}

/** Stable empty default — a fresh `new Set()` on every render would defeat
 *  the memo comparator below. */
const NO_PENDING: ReadonlySet<BoardId> = new Set();

function TeamCardImpl({
  team,
  teams,
  armedPlayerName,
  pendingRemoveIds = NO_PENDING,
  pendingAssignIds = NO_PENDING,
  nested = false,
  readOnly = false,
  onRename,
  onDelete,
  onWithdraw,
  onRemovePlayer,
  onPlaceInSlot,
}: {
  team: BoardTeam;
  teams: BoardTeam[];
  /** Name of the player selected in the rail, if any — arms the empty slots. */
  armedPlayerName?: string;
  /**
   * Players on THIS team whose removal is in flight — locks that slot's X
   * control so a second click can't fire a duplicate DELETE that 404s on an
   * already-removed player. Scoped per player rather than the whole card: the
   * cache update is optimistic, so a removal elsewhere on the team has
   * nothing for an unrelated slot to wait on.
   */
  pendingRemoveIds?: ReadonlySet<BoardId>;
  /**
   * Players with an assignment in flight, checked here too: a slot's X
   * control is also locked while that same player is mid-assignment
   * elsewhere, so a remove can't race an assign for the same id and leave
   * the two optimistic patches contradicting each other.
   */
  pendingAssignIds?: ReadonlySet<BoardId>;
  /**
   * Rendered as the body of an expanded TeamRow. The card keeps the name —
   * it holds the only rename control there is — and the row drops its own
   * while expanded, so the team is named once rather than appearing twice.
   */
  nested?: boolean;
  /** Membership is fixed: no rename, delete, remove, or drop. */
  readOnly?: boolean;
  onRename: (name: string) => void;
  onDelete: () => void;
  /** Started team leagues withdraw the whole team instead of deleting it. */
  onWithdraw?: () => void;
  onRemovePlayer: (userId: BoardId) => void;
  onPlaceInSlot: (index: number) => void;
}) {
  // Pulse only on the transition into complete, never on mount: otherwise every
  // already-built team flashes on page load, which says nothing. Adjusted
  // during render rather than in an effect — the state derives from a prop
  // change, so an effect would only add a second render pass.
  const [prevComplete, setPrevComplete] = useState(team.isComplete);
  const [justCompleted, setJustCompleted] = useState(false);
  if (prevComplete !== team.isComplete) {
    setPrevComplete(team.isComplete);
    setJustCompleted(team.isComplete);
  }

  // animationend is not guaranteed to arrive: under prefers-reduced-motion the
  // keyframes are `animation: none`, so nothing ever fires and the flag would
  // stay true for the life of the card. Harmless while it only drives a class
  // that paints nothing, but it is still state stuck on.
  useEffect(() => {
    if (!justCompleted) return;
    const t = setTimeout(() => setJustCompleted(false), PULSE_MS);
    return () => clearTimeout(t);
  }, [justCompleted]);

  return (
    <div
      onAnimationEnd={(e) => {
        if (isOwnAnimationEnd(e)) setJustCompleted(false);
      }}
      className={cn(
        "relative rounded-2xl border p-4",
        nested && "rounded-t-none border-t-0",
        team.isComplete
          ? "border-border bg-surface"
          : "border-warn/35 bg-warn/10",
        justCompleted && "animate-team-complete",
      )}
    >
      {/* Straddles the top border rather than sharing the header row, which
          left the name nowhere to go on a long team name. */}
      {!team.isComplete && !nested && team.slots.some((slot) => slot) && (
        <NeedsPartnerTag className="absolute -top-2 left-1/2 -translate-x-1/2" />
      )}

      <div className="mb-4 flex items-center justify-between gap-2">
        {readOnly ? (
          <span className="poppins text-base font-semibold break-words text-text-primary">
            {team.name}
          </span>
        ) : (
          <TeamNameEditor team={team} teams={teams} onRename={onRename} />
        )}
        <span className="flex shrink-0 items-center gap-2">
          {!readOnly && (
            <DeleteTeamButton teamName={team.name} onClick={onDelete} />
          )}
          {readOnly && onWithdraw && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onWithdraw}
            >
              Withdraw team
            </Button>
          )}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {team.slots.map((slot, i) =>
          slot ? (
            <FilledSlot
              key={slot.userId}
              player={slot}
              disabled={
                readOnly ||
                pendingRemoveIds.has(slot.userId) ||
                pendingAssignIds.has(slot.userId)
              }
              onRemove={
                readOnly ? undefined : () => onRemovePlayer(slot.userId)
              }
            />
          ) : readOnly ? (
            <EmptySlot key={`empty-${i}`} label="Open" />
          ) : (
            <DroppableEmptySlot
              key={`empty-${i}`}
              teamId={team.teamId}
              index={i}
              isArmed={!!armedPlayerName}
              label={
                team.slots.some((slot) => slot)
                  ? "Drop teammate here"
                  : "Drop player here"
              }
              onPlace={armedPlayerName ? () => onPlaceInSlot(i) : undefined}
            />
          ),
        )}
      </div>
    </div>
  );
}

/** Whether this team's own slots see the same pending-removal state in both
 *  sets — ignores ids pending removal elsewhere on the board, and ignores the
 *  sets' own identity, which changes on every pending/clear regardless of
 *  which team it belongs to. */
function samePendingForTeam(
  team: BoardTeam,
  a: ReadonlySet<BoardId>,
  b: ReadonlySet<BoardId>,
): boolean {
  if (a === b) return true;
  return team.slots.every((s) => !s || a.has(s.userId) === b.has(s.userId));
}

/**
 * `team`/`teams` are rebuilt from scratch every render of the board (see
 * `toBoardTeams`), so reference equality would never bail a card out even
 * when nothing in it changed. `teamEqual` compares the fields the card
 * actually renders instead. `teams` itself still has to be checked, just
 * more cheaply than by reference: it feeds the rename box's sibling-name
 * check, and a card that bails out keeps its *previous* render's `teams`
 * closure — so without `teamNamesEqual`, this card's own rename could
 * validate against a sibling name that's already stale and miss a
 * now-real duplicate.
 */
export const TeamCard = memo(TeamCardImpl, (prev, next) => {
  const prevRemove = prev.pendingRemoveIds ?? NO_PENDING;
  const nextRemove = next.pendingRemoveIds ?? NO_PENDING;
  const prevAssign = prev.pendingAssignIds ?? NO_PENDING;
  const nextAssign = next.pendingAssignIds ?? NO_PENDING;
  return (
    prev.nested === next.nested &&
    prev.readOnly === next.readOnly &&
    !!prev.onWithdraw === !!next.onWithdraw &&
    prev.armedPlayerName === next.armedPlayerName &&
    teamEqual(prev.team, next.team) &&
    teamNamesEqual(prev.teams, next.teams) &&
    samePendingForTeam(next.team, prevRemove, nextRemove) &&
    samePendingForTeam(next.team, prevAssign, nextAssign)
  );
});

/** Compact row used below the md breakpoint, where the 2-up grid can't fit. */
function TeamRowImpl({
  team,
  onExpand,
  isExpanded,
}: {
  team: BoardTeam;
  onExpand: () => void;
  isExpanded: boolean;
}) {
  const filled = team.slots.flatMap((s) => (s ? [s] : []));
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-expanded={isExpanded}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        team.isComplete
          ? "border-border bg-surface hover:border-primary/30"
          : "border-warn/35 bg-warn/10",
      )}
    >
      <span className="flex shrink-0 -space-x-2">
        {filled.map((p) => (
          <Avatar key={p.userId} initials={p.initials} />
        ))}
      </span>
      {/* While expanded the card below names the team and owns the rename
          control, so the row drops its name rather than printing it twice. */}
      <span className="min-w-0 flex-1">
        {!isExpanded && (
          <>
            <span className="poppins block break-words text-sm font-semibold text-text-primary">
              {team.name}
            </span>
            <span className="mulish block break-words text-xs text-text-muted">
              {filled.length > 0
                ? filled.map((p) => p.name).join(" & ")
                : "No players yet"}
            </span>
          </>
        )}
      </span>
      {!team.isComplete && <NeedsPartnerTag />}
      <ChevronRight
        className={cn(
          "size-4 shrink-0 text-text-muted transition-transform",
          isExpanded && "rotate-90",
        )}
        aria-hidden
      />
    </button>
  );
}

export const TeamRow = memo(
  TeamRowImpl,
  (prev, next) =>
    prev.isExpanded === next.isExpanded && teamEqual(prev.team, next.team),
);
